export const normalizePayload = (payload) => {
    if (payload && Object.prototype.hasOwnProperty.call(payload, "data")) {
        return payload.data;
    }
    return payload;
};

export const firstPresent = (...values) => {
    for (const value of values) {
        if (value !== undefined && value !== null && value !== "") {
            return value;
        }
    }
    return null;
};

export const dateOnly = (value) => {
    if (!value) {
        return null;
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const day = String(value.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }
    const raw = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        return raw;
    }
    const datePart = raw.split("T")[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        return datePart;
    }
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
        return dateOnly(parsed);
    }
    return null;
};

const parseDateTime = (value) => {
    if (!value) {
        return null;
    }
    const parsed = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
};

const findEarlierDateCandidate = (endValue, candidates = []) => {
    const endTime = parseDateTime(endValue);
    const endDate = dateOnly(endValue);
    return candidates.find((candidate) => {
        if (!candidate) {
            return false;
        }
        const candidateTime = parseDateTime(candidate);
        if (endTime !== null && candidateTime !== null) {
            return candidateTime < endTime;
        }
        const candidateDate = dateOnly(candidate);
        return Boolean(candidateDate && endDate && candidateDate < endDate);
    }) || null;
};

const shouldReplacePeriodStart = (startValue, endValue) => {
    if (!startValue || !endValue) {
        return false;
    }
    const startTime = parseDateTime(startValue);
    const endTime = parseDateTime(endValue);
    if (startTime !== null && endTime !== null && startTime >= endTime) {
        return true;
    }
    const startDate = dateOnly(startValue);
    const endDate = dateOnly(endValue);
    return Boolean(startDate && endDate && startDate >= endDate);
};

const diffDates = (startDate, endDate) => {
    const start = dateOnly(startDate);
    const end = dateOnly(endDate);
    if (!start || !end) {
        return null;
    }
    const startTime = new Date(`${start}T00:00:00`).getTime();
    const endTime = new Date(`${end}T00:00:00`).getTime();
    if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) {
        return null;
    }
    return Math.max(Math.round((endTime - startTime) / (24 * 60 * 60 * 1000)), 0);
};

const isCanonicalSubscriptionState = (value) => {
    return Boolean(
        value &&
        typeof value === "object" &&
        (
            value.period ||
            value.passes ||
            value.raw ||
            Object.prototype.hasOwnProperty.call(value, "can_use_paid_features")
        )
    );
};

const getOwnedSubscriptionFromProfile = (profileData = {}) => {
    return profileData?.userassignedsubscriptionmapping?.user_owned_subscription_mapping ||
        profileData?.user_owned_subscription_mapping ||
        profileData;
};

const shouldPreferCyclePeriodStart = (currentPeriodStart, cyclePeriodStart, periodEnd) => {
    const currentStartTime = parseDateTime(currentPeriodStart);
    const cycleStartTime = parseDateTime(cyclePeriodStart);
    const endTime = parseDateTime(periodEnd);
    if (currentStartTime !== null && cycleStartTime !== null) {
        return cycleStartTime > currentStartTime && (endTime === null || cycleStartTime < endTime);
    }
    const currentStartDate = dateOnly(currentPeriodStart);
    const cycleStartDate = dateOnly(cyclePeriodStart);
    const endDate = dateOnly(periodEnd);
    return Boolean(
        currentStartDate
        && cycleStartDate
        && cycleStartDate > currentStartDate
        && (!endDate || cycleStartDate < endDate)
    );
};

const getLegacySubscriptionDates = (subscription = {}) => {
    const currentPeriod = subscription.current_period || {};
    const currentPeriodStart = firstPresent(
        subscription.billing_period_start_date,
        subscription.billing_start_date,
        subscription.current_period_start,
        subscription.current_period_start_at,
        currentPeriod.current_period_start,
        currentPeriod.current_period_start_at,
        currentPeriod.start
    );
    const cyclePeriodStart = firstPresent(
        subscription.current_cycle_start_date,
        subscription.cycle_start_date
    );
    const periodEnd = firstPresent(
        subscription.billing_period_end_date,
        subscription.billing_end_date,
        subscription.current_period_end,
        subscription.current_period_end_at,
        currentPeriod.current_period_end,
        currentPeriod.current_period_end_at,
        currentPeriod.end,
        subscription.end_date
    );
    let periodStart = shouldPreferCyclePeriodStart(currentPeriodStart, cyclePeriodStart, periodEnd)
        ? cyclePeriodStart
        : firstPresent(currentPeriodStart, cyclePeriodStart, subscription.start_date);
    if (shouldReplacePeriodStart(periodStart, periodEnd)) {
        periodStart = findEarlierDateCandidate(periodEnd, [
            cyclePeriodStart,
            subscription.subscription_start_date,
            subscription.start_date,
        ]) || periodStart;
    }
    return {
        subscriptionStart: dateOnly(firstPresent(
            subscription.billing_subscription_start_date,
            subscription.subscription_start_date,
            subscription.start_date
        )),
        periodStart: dateOnly(periodStart),
        periodEnd: dateOnly(periodEnd),
    };
};

const normalizeCanonicalSubscriptionState = (state = {}) => {
    const period = state.period || {};
    const grace = state.grace || {};
    const passes = state.passes || {};
    const provider = state.provider || {};
    const plan = state.plan || {};
    const raw = state.raw || {};
    const stateName = String(state.state || state.canonical_state || "none").toLowerCase();
    const periodStart = dateOnly(period.start_date);
    const periodEnd = dateOnly(period.end_date || state.end_date);
    const graceEndDate = dateOnly(grace.ends_date || state.grace_ends_at);
    const canUsePaidFeatures = state.can_use_paid_features !== undefined
        ? Boolean(state.can_use_paid_features)
        : ["active", "cancelling", "grace_period"].includes(stateName);

    return {
        ...state,
        state: stateName,
        can_use_paid_features: canUsePaidFeatures,
        can_buy_passes: state.can_buy_passes !== undefined
            ? Boolean(state.can_buy_passes)
            : stateName === "active" && !provider.cancel_requested,
        can_renew: state.can_renew !== undefined
            ? Boolean(state.can_renew)
            : ["grace_period", "expired"].includes(stateName),
        can_cancel: state.can_cancel !== undefined
            ? Boolean(state.can_cancel)
            : stateName === "active" && Boolean(provider.provider_subscription_id),
        can_resume: state.can_resume !== undefined
            ? Boolean(state.can_resume)
            : stateName === "cancelling",
        period: {
            ...period,
            start_date: periodStart,
            end_date: periodEnd,
            days_total: period.days_total ?? diffDates(periodStart, periodEnd),
            days_remaining: period.days_remaining ?? null,
        },
        grace: {
            ...grace,
            active: grace.active !== undefined ? Boolean(grace.active) : stateName === "grace_period",
            ends_date: graceEndDate,
            ends_at: grace.ends_at || state.grace_ends_at || null,
            days_remaining: grace.days_remaining ?? null,
        },
        passes: {
            total: Number(passes.total ?? 0),
            remaining: Number(passes.remaining ?? 0),
            used: Number(passes.used ?? Math.max(Number(passes.total ?? 0) - Number(passes.remaining ?? 0), 0)),
        },
        plan,
        provider,
        raw,
        end_date: periodEnd,
        grace_ends_at: graceEndDate,
        days_remaining: state.days_remaining ?? period.days_remaining ?? grace.days_remaining ?? null,
    };
};

const buildLegacySubscriptionState = (source = {}, statusPayload = {}) => {
    const owned = getOwnedSubscriptionFromProfile(source) || {};
    const dates = getLegacySubscriptionDates(owned);
    const normalizedStatus = normalizePayload(statusPayload) || {};
    const statusState = String(normalizedStatus.canonical_state || normalizedStatus.status || "").toLowerCase();
    const ownedStatus = String(owned?.status || "").toLowerCase();
    const cancelRequested = Boolean(owned?.cancel_requested || normalizedStatus.has_pending_cancellation);
    let state = statusState || ownedStatus || "none";
    if (state === "active" && cancelRequested) {
        state = "cancelling";
    }
    const total = Number(owned?.no_of_passes ?? normalizedStatus.quantity ?? 0);
    const remaining = Number(owned?.no_of_passes_remaining ?? 0);
    return normalizeCanonicalSubscriptionState({
        state,
        timezone: owned?.billing_timezone || source?.userpreferences?.timezone_field || null,
        period: {
            start_date: dates.periodStart,
            end_date: dates.periodEnd,
            days_total: diffDates(dates.periodStart, dates.periodEnd),
            days_remaining: null,
        },
        grace: {
            active: state === "grace_period",
            ends_at: null,
            ends_date: null,
            days_remaining: null,
        },
        passes: {
            total,
            remaining,
            used: Math.max(total - remaining, 0),
        },
        plan: {
            subscription_id: owned?.subscription?.id || null,
            subscription_name: owned?.subscription?.name || null,
            plan_id: owned?.subscription_plan || null,
            plan_name: null,
            plan_days: null,
        },
        provider: {
            provider: owned?.provider || normalizedStatus.provider || null,
            provider_subscription_id: owned?.provider_subscription_id || normalizedStatus.provider_subscription_id || null,
            payment_service_subscription_id: owned?.payment_service_subscription_id || normalizedStatus.source_subscription_id || null,
            webhook_pending: Boolean(owned?.provider_webhook_pending),
            payment_confirmed: Boolean(owned?.payment_confirmed),
            cancel_requested: cancelRequested,
        },
        raw: {
            subscription_start_at: owned?.subscription_start_date || owned?.start_date || null,
            current_period_start_at: owned?.current_period_start_at || owned?.current_period_start || null,
            current_period_end_at: owned?.current_period_end_at || owned?.current_period_end || owned?.end_date || null,
            next_renewal_at: owned?.next_renewal_date || null,
        },
    });
};

export const getSubscriptionState = (source = {}, statusPayload = {}) => {
    const normalizedSource = normalizePayload(source) || {};
    const normalizedStatus = normalizePayload(statusPayload) || {};
    const owned = getOwnedSubscriptionFromProfile(normalizedSource) || {};
    const canonical = firstPresent(
        normalizedStatus.subscription_state,
        normalizedSource.subscription_state,
        owned.subscription_state,
        isCanonicalSubscriptionState(normalizedSource) ? normalizedSource : null,
        isCanonicalSubscriptionState(owned) ? owned : null
    );
    if (canonical) {
        return normalizeCanonicalSubscriptionState(canonical);
    }
    return buildLegacySubscriptionState(normalizedSource, normalizedStatus);
};

export const getSubscriptionDates = (subscription = {}) => {
    if (subscription?.subscription_state || isCanonicalSubscriptionState(subscription)) {
        const state = getSubscriptionState(subscription);
        return {
            subscriptionStart: dateOnly(state?.period?.start_date),
            periodStart: dateOnly(state?.period?.start_date),
            periodEnd: dateOnly(state?.period?.end_date),
        };
    }
    return getLegacySubscriptionDates(subscription);
};

export const formatPaymentPasses = (row = {}, rows = []) => {
    const typeKey = getPaymentTypeKey(row, rows);
    const added = row?.passes_added;
    const total = row?.total_passes;
    if (typeKey === "renewal" && total != null) {
        return `Renewed ${total}`;
    }
    if (added == null && total == null) {
        return "-";
    }
    if (added != null && total != null) {
        return `+${added} / ${total}`;
    }
    if (total != null) {
        return `${total}`;
    }
    return `+${added}`;
};

const getPaymentTimestamp = (row = {}) => {
    const value = firstPresent(row?.created_at, row?.paid_date, row?.updated_at);
    if (!value) {
        return null;
    }
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
};

export const hasEarlierSubscriptionPayment = (row = {}, rows = []) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return false;
    }
    const rowTime = getPaymentTimestamp(row);
    return rows.some((item) => {
        if (!item || item === row || item.id === row.id) {
            return false;
        }
        const typeKey = getPaymentTypeKey(item);
        if (!["new_subscription", "renewal"].includes(typeKey)) {
            return false;
        }
        const status = String(item?.status || "").toLowerCase();
        if (status && !["paid", "success", "captured"].includes(status)) {
            return false;
        }
        const itemTime = getPaymentTimestamp(item);
        if (rowTime !== null && itemTime !== null) {
            return itemTime < rowTime;
        }
        return true;
    });
};

export const getPaymentTypeKey = (row = {}, rows = []) => {
    const explicitType = String(row?.payment_type || "").toLowerCase();
    if (explicitType) {
        if (explicitType === "new_subscription" && hasEarlierSubscriptionPayment(row, rows)) {
            return "renewal";
        }
        return explicitType;
    }
    const kind = String(row?.kind || "").toLowerCase().split(".").pop();
    const status = String(row?.status || "").toLowerCase();
    if (["failed", "cancelled", "canceled", "expired"].includes(status)) {
        return "failed";
    }
    if (kind === "upgrade") {
        return "buy_passes";
    }
    if (kind === "subscription_cycle") {
        return "renewal";
    }
    if (kind === "standard") {
        if (hasEarlierSubscriptionPayment(row, rows)) {
            return "renewal";
        }
        return "new_subscription";
    }
    return "subscription";
};

export const getPaymentAmountMinor = (row = {}) => {
    const explicitMinor = firstPresent(row?.amount_minor, row?.amount_cents, row?.amount_paise);
    if (explicitMinor !== null) {
        const parsed = Number(explicitMinor);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    if (row?.amount_major !== undefined && row?.amount_major !== null && row?.amount_major !== "") {
        const parsedMajor = Number(row.amount_major);
        return Number.isFinite(parsedMajor) ? Math.round(parsedMajor * 100) : 0;
    }
    if (row?.amount !== undefined && row?.amount !== null && row?.amount !== "") {
        const parsed = Number(row.amount);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    if (row?.calculated_amount !== undefined && row?.calculated_amount !== null && row?.calculated_amount !== "") {
        const parsedMajor = Number(row.calculated_amount);
        return Number.isFinite(parsedMajor) ? Math.round(parsedMajor * 100) : 0;
    }
    return 0;
};

export const getPaymentTypeLabel = (row = {}, rows = []) => {
    const typeKey = getPaymentTypeKey(row, rows);
    let label = "Subscription";
    if (typeKey === "new_subscription") label = "New Subscription";
    if (typeKey === "renewal") label = "Renewal";
    if (typeKey === "buy_passes") label = "Buy Passes";
    if (typeKey === "failed") label = "Failed";
    return label;
};

export const normalizePaymentPayload = (payload) => {
    const normalized = normalizePayload(payload);
    if (Array.isArray(normalized)) {
        return {count: normalized.length, results: normalized};
    }
    if (normalized && Array.isArray(normalized.results)) {
        return {
            count: normalized.count ?? normalized.results.length,
            results: normalized.results,
        };
    }
    if (normalized && Array.isArray(normalized.payments)) {
        return {
            count: normalized.total ?? normalized.count ?? normalized.payments.length,
            results: normalized.payments,
        };
    }
    return normalized || {count: 0, results: []};
};

export const isSubscriptionProcessingComplete = (ownedSubscription, statusPayload = {}) => {
    if (!ownedSubscription) {
        return false;
    }
    const normalizedStatus = normalizePayload(statusPayload) || {};
    const statusState = getSubscriptionState(
        {subscription_state: ownedSubscription.subscription_state || null},
        normalizedStatus
    );
    const status = String(normalizedStatus.status || "").toLowerCase();
    const canonical = String(normalizedStatus.canonical_state || "").toLowerCase();
    if (ownedSubscription.provider_webhook_pending === true) {
        return false;
    }
    if (ownedSubscription.payment_confirmed === true) {
        return true;
    }
    if (ownedSubscription.callback_received === false) {
        return true;
    }
    if (["active", "cancelling", "grace_period"].includes(statusState?.state)) {
        return true;
    }
    return ["active", "renewed"].includes(status) || canonical === "active";
};
